import { Component, h, Prop, State, setMode } from '@stencil/core';
import { waitUntil } from 'atc-js';

// utils
import { loadFonts, caiEvent } from '@/utils';

// analytics
import analytics from '@/analytics';

import { ELOT_ANALYTICS, ELOT_GET_DATA, ELOT_UPDATE_LISTINGS } from '@/constants/elotEvents';

import { fetchDealerLot } from '@/apis/dealerLotApi';

// types
import { InventoryType, OwnerType, AppDataType, LocationType } from '@/types';

// components
import ListingCard from '../ListingCard';
import DealerHeader from '../DealerHeader';

// set only once time when Component is initialized
setMode(el => (el as any).brand || el.getAttribute('brand') || 'atc');

@Component({
    tag: 'dealer-lot',
    styleUrls: {
        atc: 'atc-dealer-lot.scss',
        kbb: 'kbb-dealer-lot.scss',
    },
    shadow: true,
})

export class DealerLot {

    @State() inventory: Array<InventoryType> = [];
    @State() owner: OwnerType;
    @State() location: LocationType;
    @State() whitelabel: boolean = false;

    @Prop() brand: 'atc' | 'kbb';

    constructor() {
        this.handleCarouselSettle = this.handleCarouselSettle.bind(this);
        this.handleCarouselReady = this.handleCarouselReady.bind(this);
        this.getAppData = this.getAppData.bind(this);
        this.parseOptions = this.parseOptions.bind(this);
    }

    parseOptions(options: AppDataType) {
        const { zip, pageName, pageId, whitelabel, linkUrl, ...apiData } = options;

        return {
            analyticsOptions: {
                brand: this.brand,
                zip,
                pageName,
                pageId,
            },
            apiOptions: {
                ...apiData,
                zip
            },
            whitelabel: whitelabel === true || whitelabel === 'true',
            linkUrl,
        }
    };

    /**
     * Will trigger get the elot data if the ELOT_GET_DATA event is registered
     */
    private triggerGetElotData(): void {
        let isRegisteredEvent = false;
        const checkDelay = 100;
        const waitTimeout = 10000;

        const checkGetDataEventRegistered = (): boolean => {
            if (!isRegisteredEvent) {
                caiEvent.checkEvent(ELOT_GET_DATA).then((result) => {
                    isRegisteredEvent = result?.length > 0
                });
            }
            return isRegisteredEvent;
        }

        const onRegisterd = () => caiEvent.send(ELOT_GET_DATA, this.getAppData);

        const onTimeout = () => console.error("ELOT_GET_DATA not registered");

        waitUntil(
            checkGetDataEventRegistered,
            onRegisterd,
            checkDelay,
            waitTimeout,
            onTimeout,
        );
    }

    private async getAppData(options: AppDataType) {
        try {
            // initialize analytics metadata and listeners
            const { apiOptions, analyticsOptions, linkUrl, whitelabel } = this.parseOptions(options);
            analytics.init(analyticsOptions);

            this.whitelabel = whitelabel;

            const { listings = [], owner, location } = await fetchDealerLot(apiOptions);

            // Transform website.href if linkUrl is provided
            const transformedListings = listings.map(listing => {
                if (listing.website?.href && linkUrl) {
                    // Remove protocol and server name, keep only the path
                    const url = new URL(listing.website.href);
                    const pathOnly = url.pathname + url.search + url.hash;
                    
                    // Replace with linkUrl
                    listing.website.href = linkUrl + pathOnly;
                }
                return listing;
            });

            const filtedListing = transformedListings.filter(item => item?.pricingDetail?.salePrice > 0)

            // TODO: should inventory/owner be separated from analytics.js?
            analytics.addInventory(filtedListing);

            this.inventory = filtedListing;
            this.location = location;

            // TODO: This comes back undefined on multiple dealers version
            // only assigning if it exists for now
            if (owner) {
                analytics.addOwner(owner);
                // TODO: this may not be needed..
                analytics.setActiveOwner(owner?.id);
                this.owner = owner;
            }

        } catch (error) {
            console.log('FETCH ERROR: ', error);
        }
    }

    async componentWillLoad() {
        loadFonts({ brand: this.brand });

        this.triggerGetElotData();

        // fetch new listings based on new data
        caiEvent.listen(ELOT_UPDATE_LISTINGS, this.getAppData);
    }

    disconnectedCallback() {
        // disconnect the event handler on component unmount.
        caiEvent.unlisten(ELOT_UPDATE_LISTINGS, this.getAppData);
    }

    sendInventoryImpressions({ selectedIndicies }) {
        const selectedItemIds = selectedIndicies.reduce((acc, index) => {
            const inventoryId = this.inventory?.[index]?.id;
            if (inventoryId) {
                acc.push(inventoryId);
            }
            return acc;
        }, []);

        caiEvent.send(ELOT_ANALYTICS, {
            action: 'inventoryImpressions',
            data: {
                selectedItemIds,
            }
        });
    }

    sendOwnerImpressions() {
        caiEvent.send(ELOT_ANALYTICS, {
            action: 'ownerImpressions',
            data: {
                ownerIds: [this.owner.id],
            },
        });
    }

    handleCarouselSettle(data) {
        this.sendInventoryImpressions(data);
    }

    handleCarouselReady(data) {
        this.sendInventoryImpressions(data);

        if (this.owner?.id) {
            this.sendOwnerImpressions();
        }
    }

    render() {
        return this.inventory && this.inventory.length > 0 ? (
            <div class="web-container">
                <axl-carousel
                    id="carousel"
                    fullHeight
                    // TODO: figure out how to pull in axl-config sizes and remove hardcoded value
                    cellWidth='200px'
                    items={this.inventory}
                    callbackOnSettle={this.handleCarouselSettle}
                    callbackOnReady={this.handleCarouselReady}
                    renderCell={(item) => {
                        return <ListingCard brand={this.brand} inventory={item} owner={item.owner} whitelabel={this.whitelabel} />
                    }}
                    renderTitle={() => !this.whitelabel && <DealerHeader owner={this.owner} inventory={this.inventory} location={this.location} />}
                />
            </div>
        ) : null;
    }
}